import clsx from "clsx";

import { RLCard, RenderWhen, TooltipIcon } from "../../../../components";
import { ClassificationImage } from "../../../commons/header";
import { useGetReportSummary, useLocalSampleSummaryParams } from "../../hooks";
import { ClassificationReasonCard } from "./classification-reason";
import { Detections, MultiScannerCard, YaraDetectionsCard } from "./detections";
import { Info } from "./info";
import { RiskScoreCard } from "./risk-score";
import { ThreatInfoCard } from "./threat-info";

import { getNumberOfYaraMatches } from "../report_summary/cards/yara_matches/utils";
import styles from "./summary-header.module.scss";
import useParanoidMode from "../../../paranoid-mode/hooks/useParanoidMode";
import classificationByLabels, {
  getClassificationByLabels
} from "../../../../lib/enums/ThreatStatusEnum";

const ReportSummaryHeader = () => {
  const { hash, sourceId, relationshipId } = useLocalSampleSummaryParams();
  const { data } = useGetReportSummary(hash, sourceId, relationshipId);
  const { paranoidModeConfig } = useParanoidMode();

  if (!data) return null;

  const showParanoidModeGoodwareExplanation =
    getClassificationByLabels(data.threat_status) ===
      classificationByLabels.GOODWARE && (paranoidModeConfig?.level ?? 0) > 0;

  return (
    <header
      className={`${styles.summary_header} ${styles[data.threat_status]}`}
      data-testid="report_summary_header_container"
    >
      <ClassificationImage
        classification={data.threat_status}
        fileType={data.file_type_image_name}
      />
      <RLCard
        className={styles.summary_header_card}
        content={
          <RLCard.Content>
            <Info
              fileType={data.file_type}
              fileFormat={data.file_format}
              fileSize={data.file_size}
            />
          </RLCard.Content>
        }
        style={{ border: "none" }}
        data-testid="report_summary_header_info_card"
      />
      <RLCard
        className={styles.summary_header_card}
        header={
          <RLCard.Header className={styles.card_title}>
            Risk Score
          </RLCard.Header>
        }
        content={
          <RLCard.Content style={{ padding: "0.25rem" }}>
            <RiskScoreCard
              classification={data.threat_status}
              factor={data.factor}
            />
          </RLCard.Content>
        }
        data-testid="report_summary_header_risk_score_card"
      />
      <RLCard
        className={styles.summary_header_card}
        header={
          <RLCard.Header className={styles.card_title}>
            Threat Name / Actor
          </RLCard.Header>
        }
        content={
          <RLCard.Content>
            <ThreatInfoCard name={data.threat_name} />
          </RLCard.Content>
        }
        data-testid="report_summary_header_threat_name_card"
      />
      <RLCard
        className={styles.summary_header_card}
        header={
          <RLCard.Header className={styles.card_title}>
            Classification Reason
            <RenderWhen condition={showParanoidModeGoodwareExplanation}>
              <TooltipIcon
                icon="info-circle"
                message="Increasing Risk Tolerance sensitivity expands the use of additional signals, but final classification still prioritizes high-confidence indicators. Files with strong evidence of being safe may be classified as Goodware regardless of the selected Risk Tolerance level."
                className="ms-5"
                placement="bottom"
              />
            </RenderWhen>
          </RLCard.Header>
        }
        content={
          <RLCard.Content
            className={clsx(
              Boolean(data.classification_details.length) && "cursor-pointer"
            )}
          >
            <ClassificationReasonCard
              classificationReason={data.classification_reason.title}
              classificationReasonStyle={data.classification_reason.style}
              classificationSource={data.classification_source}
              hasFileClassification={Boolean(
                data.classification_details.length
              )}
              threatStatus={data.threat_status}
            />
          </RLCard.Content>
        }
        data-testid="report_summary_header_classification_reason_card"
      />
      <RLCard
        className={styles.summary_header_card}
        header={
          <RLCard.Header className={styles.card_title}>
            Detections
          </RLCard.Header>
        }
        content={
          <RLCard.Content style={{ padding: "0" }}>
            <Detections>
              <MultiScannerCard
                count={data.av_scanners?.scanner_count ?? 0}
                match={data.av_scanners?.scanner_match ?? 0}
              />
              <YaraDetectionsCard
                matches={getNumberOfYaraMatches(data?.yara_matches)}
              />
            </Detections>
          </RLCard.Content>
        }
        data-testid="report_summary_header_detections_card"
      />
    </header>
  );
};

export default ReportSummaryHeader;
